import React, { useState, useEffect } from 'react';
import { PrimaryButton, SecondaryButton } from "../UI";
import ConfigureExperience from '../Experience/ConfigureExperience';
import ChooseTemplate from '../Template/ChooseTemplate';
import tickImg from '../../assets/tick-step.svg';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { createExperience, createTenantActivityOffers, delinkOfferFromExperience, editExperience, editExperienceOfferMapping, getOfferExperienceMappings, getTenantActivityOffers, updateTenantActivityOffers } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import Loader from '../UI/Loader/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import { compareJson, getObjFromHtml, isValidJsonString } from '../../utils/helper';
import OfferCode from './offerCode';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import Input from '../UI/Input';
import { permission } from '../../config/permission';
import { toast } from 'react-toastify';
import backwardArrow from '../../assets/icons/backward-arrow.svg';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import DropdownButton from './DropdownButton';
import CreateExperienceModal from './CreateExperienceModal';
import Activation from '../ExperienceCode/Activation';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import UpdateOfferAssociationModal from './UpdateOfferAssociationModal';

const EditOffer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enums, content } = useContentfulContext();
  const { enablePreviewMode, disablePreviewMode } = usePreviewMode();

  const [currentStep, setCurrentStep] = useState('choose-template');
  const [offerName, setOfferName] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { userData, checkPermission } = useUserContext();
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [fieldValues, setFieldValues] = useState();
  const [transformedExperienceJSON, setTransformedExperienceJSON] = useState();
  const [offerJson, setOfferJson] = useState({ offerType: id?.split?.("-")[0] })
  const [originalOfferJson, setOriginalOfferJson] = useState({ offerType: id?.split?.("-")[0] });
  const [validationError, setValidationError] = useState("");
  const [dynamicFields, setDynamicFields] = useState({})
  const [, setOfferId] = useState();
  const [experience, setExperience] = useState(null);
  const [showCreateExperienceModal, setShowCreateExperienceModal] = useState(null);
  const [offerSuccess, setOffersuccess] = useState("");

  const [showUpdateOfferModal, setShowUpdateOfferModal] = useState(false);
  const [associatedExperiences, setAssociatedExperiences] = useState([]);

  useEffect(() => {
    getOfferJson()
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setError('');
      setSuccessMessage('');
    }, 4000);

    return () => clearTimeout(timer);
  }, [error, successMessage]);

  const getOfferJson = async () => {
    try {
      setLoading(true);
      const result = await getTenantActivityOffers(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        { tenant: userData.defaultAdobeIO?.tenant_name, ids: id?.split("-")[1], type: id?.split?.("-")[0] }
      );
      setOfferJson((prev) => ({ ...prev, ...result.data[0] }))
      setOriginalOfferJson({ ...originalOfferJson, ...result.data[0] })
      setOfferName(result.data[0].name)

      // Fetch associated experiences for this offer
      const mappings = await getOfferExperienceMappings(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        result.data[0].id,
        userData?.defaultAdobeIO?._id
      );

      setAssociatedExperiences(mappings?.data || []);
    } catch (error) {
      console.log('error', error)
      toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg)
      navigate('/experience/manage-offers')
    } finally {
      setLoading(false);
    }
  }

  function getFlattenExperienceJSON(ob) {
    let result = {};
    function recurse(cur, prop) {
      if (Object(cur) !== cur) {
        if (dynamicFields[prop] !== undefined) {
          result[prop] = fieldValues[prop];
          setDynamicFields((prev) => ({
            ...prev,
            [prop]: cur
          }))
        } else {
          result[prop] = cur;
        }
      } else if (Array.isArray(cur)) {
        for (let i = 0; i < cur.length; i++) {
          recurse(cur[i], prop ? prop + "[" + i + "]" : i);
        }
        if (cur.length === 0) result[prop] = [];
      } else {
        let isEmpty = true;
        for (let p in cur) {
          isEmpty = false;
          recurse(cur[p], prop ? prop + "." + p : p);
        }
        if (isEmpty && prop) result[prop] = {};
      }
    }
    recurse(ob, "");
    return result;
  }

  const handleUpdateOffer = async (showLoader = true) => {
    try {
      if (!offerName) {
        setError(content.EDIT_OFFER_CONTENT.errors.name_required)
        return;
      }
      if (validationError) return;

      const isValidate = handleValidateJson();
      if (isValidate.length > 0) return;

      if (showLoader) setLoading(true);
      const payload = {
        type: id.split("-")[0],
        data: {
          id: id.split("-")[1],
          name: offerName,
          content: selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id ? offerJson?.content : transformOfferJSON(),
        },
        tenant: userData.defaultAdobeIO?.tenant_name,
      }
      const result = await updateTenantActivityOffers(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        payload
      );
      setOfferName(result.data.name);
      setError('');
      setSuccessMessage(content.EDIT_OFFER_CONTENT.success.update);
      setTimeout(() => {
        navigate("/experience/manage-offers")
      }, 2000);
    } catch (error) {
      console.log('error', error)
      setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const handleSaveAsNewOffer = async (offer) => {
    try {
      setLoading(true);
      const result = await createTenantActivityOffers(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        {
          tenant: userData.defaultAdobeIO?.tenant_name,
          data: {
            name: offer,
            workspace: userData.defaultWorkspace?.workspace_id,
            content: selectedTemplate.type === enums?.templateTypes.json_a_to_html.id
              ? offerJson?.content
              : transformOfferJSON(),
            type: selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id ? 'content' : 'json',
          },
        }
      );
      if (result?.status) {
        toast.success(result?.message || "New offer created successfully");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to create new offer");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAssociatedExperiences = async () => {
    if (associatedExperiences.length <= 0) {
      toast.error("No associated experiences to update.");
      return;
    }
    try {
      setLoading(true);
      await Promise.all(
        associatedExperiences.map(exp => {
          const payload = {
            name: exp.name,
            templates: [selectedTemplate._id],
            html: '',
            css: '',
            config_json: '',
            experience_json: selectedTemplate.type === enums?.templateTypes.json_a_to_html.id
              ? offerJson?.content
              : JSON.stringify(transformOfferJSON()),
            version: exp.published_version   // for updating published version which can update all associated activations
          };
          return editExperience(
            userData?.token,
            userData?.selectedOrganization?.org_token,
            exp.experience_id,
            userData?.defaultAdobeIO?._id,
            userData?.defaultContentful?._id,
            userData?.defaultTealiumKey?._id,
            payload
          );
        })
      );
      toast.success("Associated experiences updated successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to update associated experiences");
    } finally {
      setLoading(false);
    }
  };

  const handleDelinkExperience = async () => {
    if (associatedExperiences.length <= 0) {
      toast.error("No associated experiences to delink.");
      return;
    }
    try {
      setLoading(true);
      await Promise.all(
        associatedExperiences.map(exp =>
          delinkOfferFromExperience(
            userData?.token,
            userData?.selectedOrganization?.org_token,
            {
              experience_id: exp.experience_id,
              id: offerJson?.id,
              integration_id: userData?.defaultAdobeIO?._id,
              destination: "adobe_target",
            }
          )
        )
      );
      setAssociatedExperiences([]);
      await handleUpdateOffer(false);
      toast.success("Offer delinked from associated experiences");
    } catch (err) {
      toast.error(err?.message || "Failed to delink offer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentStep == 'choose-template' && selectedTemplate._id) {
      if (!isValidJsonString(selectedTemplate?.json_schema)) {
        setError(content.EDIT_OFFER_CONTENT.errors.valid_json)
        return;
      }

      if (selectedTemplate.type === enums?.templateTypes.json_a_to_html.id) {
        const parsedJsonSchema = JSON.parse(selectedTemplate?.json_schema)
        const updatedExperienceJson = getObjFromHtml(originalOfferJson?.content, parsedJsonSchema?.config_obj_key)
        if (!updatedExperienceJson) {
          setError(`${content.EDIT_OFFER_CONTENT.errors.config_obj_not_found}`)
          return;
        }
        offerJson.content = updatedExperienceJson;
      }

      const temp = JSON.parse(selectedTemplate?.json_schema);
      delete temp?.config_obj_key

      const compare = compareJson(temp, offerJson?.content);
      if (compare.length == 0) {
        let flattenJSON = getFlattenExperienceJSON(offerJson?.content)

        let temps = {}
        for (const key in flattenJSON) {
          const element = JSON.parse(selectedTemplate.config_json).find((x) => {
            return x.id === key;
          })

          if (element?.input?.input_type === "dynamic-text") {
            temps[element.id] = flattenJSON[key]
            flattenJSON[key] = element.input?.input_default_value;
          }
        }

        setDynamicFields(temps)
        setFieldValues(flattenJSON);

      } else {
        setError(`${compare.join(", ")}${content.EDIT_OFFER_CONTENT.errors.templates_not_same}`)
        return;
      }

      if (currentStep == 'choose-template') {
        setCurrentStep('configure-offer');
      }
    }
  }, [selectedTemplate])

  const handleNext = () => {
    if (currentStep == 'choose-template' && !selectedTemplate._id) {
      setError(content.EDIT_OFFER_CONTENT.errors.template_required);
      return;
    }
    if (currentStep == 'choose-template' && selectedTemplate._id) {
      if (!isValidJsonString(selectedTemplate?.json_schema)) {
        setError(content.EDIT_OFFER_CONTENT.errors.valid_json)
        return;
      }
      // if(id.split("-")[0].toLowerCase() != "json") {
      //   setError(content.EDIT_OFFER_CONTENT.errors.offer_type_not_json)
      //   return;
      // }

      if (selectedTemplate.type === enums?.templateTypes.json_a_to_html.id) {
        const parsedJsonSchema = JSON.parse(selectedTemplate?.json_schema)
        const updatedExperienceJson = getObjFromHtml(offerJson?.content, parsedJsonSchema?.config_obj_key)
        offerJson.content = updatedExperienceJson;
      }

      const temp = JSON.parse(selectedTemplate?.json_schema);
      delete temp?.config_obj_key

      const compare = compareJson(temp, offerJson?.content);
      if (compare.length == 0) {
        let res = getFlattenExperienceJSON(offerJson?.content)
        setFieldValues(res);

      } else {
        setError(`${compare.join(", ")}${content.EDIT_OFFER_CONTENT.errors.templates_not_same}`)
        return;
      }
    }
    if (currentStep == 'choose-template') {
      setCurrentStep('configure-offer');
    } else if (currentStep == 'configure-offer') {
      setCurrentStep('update-offer');
    }
  }

  function transformOfferJSON() {
    const transformedJSON = {};
    for (const key in fieldValues) {
      const keys = key.split('.');
      keys.reduce((acc, part, index) => {
        if (part.includes('[')) {
          const [arrayKey, arrayIndex] = part.split(/\[|\]/).filter(Boolean);
          if (!acc[arrayKey]) {
            acc[arrayKey] = [];
          }
          if (!acc[arrayKey][arrayIndex]) {
            acc[arrayKey][arrayIndex] = {};
          }
          if (index === keys.length - 1) {
            // acc[arrayKey][arrayIndex] = fieldValues[key];
            if (dynamicFields[key] !== undefined) {
              acc[arrayKey][arrayIndex] = dynamicFields[key];
            } else {
              acc[arrayKey][arrayIndex] = fieldValues[key];
            }
          }
          return acc[arrayKey][arrayIndex];
        } else {
          if (index === keys.length - 1) {
            // acc[part] = fieldValues[key];
            if (dynamicFields[key] !== undefined) {
              acc[part] = dynamicFields[key];
            } else {
              acc[part] = fieldValues[key];
            }
          } else {
            if (!acc[part]) {
              acc[part] = {};
            }
            return acc[part];
          }
        }
      }, transformedJSON);
    }
    return transformedJSON;
  }

  const handleValidateJson = () => {
    setValidationError("");

    const config_elems = [];
    JSON.parse(selectedTemplate.config_json).forEach((el) => {
      el.error = "";
      config_elems.push({ ...el, id: el.id });
    });
    const newConfigElems = [...config_elems];
    const errs = [];

    for (let item of newConfigElems) {
      let val = "";
      if (dynamicFields[item.id] !== undefined) {
        val = dynamicFields[item.id]
      } else {
        val = fieldValues[item.id];
      }

      const rules = item.rules;
      if (rules.is_mandatory) {
        if (val?.length == 0) {
          const message = "Field is required";
          item.error = message;
          errs.push(`${item.id}: ${message}`)
        } else {
          item.error = "";
        }
      }
      if (rules.min_length && rules.max_length && item.error.length == 0) {
        if (val.length > rules.max_length || val.length < rules.min_length) {
          const message = `Min length: ${rules.min_length} and Max length: ${rules.max_length}`;
          item.error = message;
          errs.push(`${item.id}: ${message}`);
        } else {
          item.error = "";
        }
      }
    }

    if (errs.length > 0) {
      setValidationError(errs.join(", \n"));
    }
    return errs;
  }

  const openCreateExperienceModal = () => {
    // if (!handleCheckValidation()) return;

    const isValidate = handleValidateJson();
    if (isValidate.length > 0) {
      setError(isValidate.join(", \n"))
      return;
    }
    setError("")
    setShowCreateExperienceModal(true)
  }

  const createExperienceHandler = async (values) => {
    try {
      const isValidate = handleValidateJson();
      if (isValidate.length > 0) {
        setError(isValidate.join(", \n"))
        return;
      }

      if (validationError) {
        setError(validationError);
        return
      }
      setLoading(true);
      const payload = {
        name: values.experience_name,
        templates: [selectedTemplate._id],
        html: '',
        css: '',
        config_json: '',
        experience_json: selectedTemplate.type == enums.templateTypes.json_a_to_html.id ? offerJson?.content : JSON.stringify(transformOfferJSON()),
        source: enums.experience_sources.offer_page.id,
        ...(offerJson?.id && { do_publish: true })   // flag will publish the created version
      }
      const result = await createExperience(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        payload
      );

      if (result?.data?.length > 0) {
        const offerMappingPayload = {
          experience_id: result.data[0]?._id,
          id: offerJson?.id,
          integration_id: userData?.defaultAdobeIO?._id,
          destination: "adobe_target"
        };
        await editExperienceOfferMapping(userData?.token, userData?.selectedOrganization?.org_token, offerMappingPayload);
      }
      setExperience(result?.data)
      setError("")
      setOffersuccess(content.EDIT_OFFER_CONTENT.success.experience)
    } catch (error) {
      setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  }

  const handleAction = (action) => {
    switch (action) {
      case content.EDIT_OFFER_CONTENT.dropdown_btn.update_offer.id:
        setShowUpdateOfferModal(true);
        // handleUpdateOffer()
        break;
      case content.EDIT_OFFER_CONTENT.dropdown_btn.activate_at.id:
        setCurrentStep("activate")
        break;
      case content.EDIT_OFFER_CONTENT.dropdown_btn.save_experience.id:
        openCreateExperienceModal()
        break;
      default:
        console.log(`Unknown action: ${action}`);
    }
  }

  // const createOffer = async (values) => {
  //   try {
  //     if(validationError) return;

  //     if(showPreview) {
  //       const isValidate = handleValidateJson();
  //       if (isValidate.length > 0) return;
  //     }
  //     const content = JSON.parse(offerJson?.content)

  //     const payload = { 
  //       tenant: userData.defaultAdobeIO?.tenant_name,
  //       data: {
  //         name: values.offer_name,
  //         workspace: userData.defaultWorkspace?.workspace_id,
  //         content: selectedTemplate.type === enums?.templateTypes.json_a_to_html.id ? offerJson?.content : content,
  //         type: selectedTemplate.type === enums.templateTypes.json_a_to_html.id ? 'content' : 'json',
  //       }
  //     }
  //     const result = await createTenantActivityOffers(
  //       userData?.token,
  //       userData?.selectedOrganization?.org_token,
  //       payload
  //     )
  //     return result;
  //   } catch (error) {
  //     throw error;
  //   }
  // };

  useEffect(() => {
    if (currentStep == "configure-offer") {
      enablePreviewMode();
    } else {
      disablePreviewMode();
    }
  }, [currentStep])

  return (
    <div className='w-full mx-auto overflow-auto sticky z-[15]'>
      {loading && <Loader />}
      {error && <ErrorAlert timeout="3000">{error}</ErrorAlert>}
      {successMessage && <SuccessAlert disappearTime={4000}>{successMessage}</SuccessAlert>}

      <div className='py-2 px-4 md:px-8 bg-gray-1 mb-2 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4'>
        <div className='w-full md:max-w-[260px]'>
          <Input
            size="sm"
            disabled
            id="offer_name"
            name="offer_name"
            value={offerName}
            placeholder={content.EDIT_OFFER_CONTENT.offer_name}
          />
        </div>
        <ol className="flex items-center justify-center flex-1 text-xs font-medium text-center text-gray-500 gap-2 md:gap-0">
          <li className="flex md:w-full items-center sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10">
            <button className="text-xs flex items-center flex-col after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200" onClick={() => {
              setOfferJson({ ...originalOfferJson })
              setCurrentStep("choose-template");
              setSelectedTemplate({})
              setFieldValues()
            }}
            >
              <img alt='' src={tickImg} className="w-4 h-4" />
              <span className="hidden lg:block">{content.EDIT_OFFER_CONTENT.choose_template}</span>
            </button>
          </li>
          <li className="flex items-center">
            <button className="text-xs flex items-center flex-col" onClick={() => {
              if (currentStep == "update-offer") {
                setCurrentStep("configure-offer")
              }
            }}>
              {(currentStep == 'configure-offer' || currentStep == 'update-offer' || currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">2</span>}
              <span className="hidden lg:block">{content.EDIT_OFFER_CONTENT.configure_offer}</span>
            </button>
          </li>
          <li className="flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
            <button className="text-xs flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
              {(currentStep == 'update-offer' || currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">3</span>}
              <span className="hidden lg:block">{content.EDIT_OFFER_CONTENT.update_offer}</span>
            </button>
          </li>
          <li className="flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
            <button className="text-xs flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
              {(currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">4</span>}
              <span className="hidden lg:block">{content.EDIT_OFFER_CONTENT.activate}</span>
            </button>
          </li>
        </ol>
        <div className='flex justify-end gap-2 md:gap-3 w-full md:w-auto'>
          {currentStep !== 'activate' && <SecondaryButton size={'base'} handleClick={() => navigate("/experience/manage-offers")}>{content.EDIT_OFFER_CONTENT.cancel_btn}</SecondaryButton>}
          {currentStep == "update-offer" ? (
            checkPermission(permission.EDIT_OFFER) && <DropdownButton handleSubmit={handleAction} />
          ) : (currentStep !== "choose-template" && currentStep !== 'activate') ? (
            <PrimaryButton size={'base'} handleClick={handleNext}>{content.EDIT_OFFER_CONTENT.next_btn}</PrimaryButton>
          ) : null}
        </div>
      </div>
      <div className='px-4 md:px-8'>
        {currentStep == 'choose-template' && offerJson?.content && (
          <ChooseTemplate
            title={content.CHOOSE_TEMPLATE_CONTENT.offer_content}
            offerJson={{ ...offerJson }}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
          />
        )}
        {currentStep == 'configure-offer' && (
          <ConfigureExperience
            setCurrentStep={setCurrentStep}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            setTransformedExperienceJSON={setTransformedExperienceJSON}
            actionText={"Edit Experience"}
            dynamicFields={dynamicFields}
            setDynamicFields={setDynamicFields}
          />
        )}
        {currentStep == 'update-offer' &&
          <OfferCode
            experienceJSON={transformedExperienceJSON}
            selectedTemplate={selectedTemplate}
            offerJson={offerJson}
            setOfferJson={setOfferJson}
            getFlattenExperienceJSON={getFlattenExperienceJSON}
            setFieldValues={setFieldValues}
            validationError={validationError}
            setValidationError={setValidationError}
            handleValidateJson={handleValidateJson}
            dynamicFields={dynamicFields}
          />
        }
        {currentStep == 'activate' &&
          <Activation
            experience={experience}
            setOfferId={setOfferId}
            setExperience={setExperience}
            validationError={validationError}
            setValidationError={setValidationError}
            handleValidateJson={handleValidateJson}
            offerPermission={{
              create: permission.CREATE_EXP_OP_CREATE_OFFER,
              edit: permission.CREATE_EXP_OP_EDIT_OFFER
            }}
            contentfulPermission={{
              create: permission?.CREATE_EXP_OP_CREATE_CONTENTFUL
            }}
            selectedTemplate={selectedTemplate}
            code={selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id ? JSON.stringify(offerJson?.content) : transformOfferJSON()}
          />
        }
        {showCreateExperienceModal && <CreateExperienceModal
          error={error}
          loading={loading}
          validationError={validationError}
          handleSubmit={createExperienceHandler}
          experienceSuccessMessage={offerSuccess}
          setShowCreateExperienceModal={setShowCreateExperienceModal}
        />}

        {showUpdateOfferModal && (
          <UpdateOfferAssociationModal
            isOpen={showUpdateOfferModal}
            onClose={() => setShowUpdateOfferModal(false)}
            associatedExperiences={associatedExperiences}
            onSaveAsNewOffer={(offerName) => handleSaveAsNewOffer(offerName)}
            onUpdateAssociatedExperience={handleUpdateAssociatedExperiences}
            onDelinkExperience={handleDelinkExperience}
            loading={loading}
          />
        )}
      </div>
    </div>
  )
}

export default EditOffer;